import {
  ContentConsentTypeEnum,
  ProgrammeTypeDto,
  ClassroomDto,
  SiteAddressDto,
  ProvinceDto,
} from '@ecdlink/core';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  ButtonGroup,
  Typography,
  ButtonGroupTypes,
  renderIcon,
  BannerWrapper,
  Checkbox,
  ImageInput,
  Dialog,
  DialogPosition,
  FormInput,
  Dropdown,
  classNames,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useFormState, useWatch, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { staticDataSelectors } from '@store/static-data';
import * as styles from './programme-details.styles';
import { yesNoOptions } from './programme-details.types';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import Article from '@/components/article/article';
import {
  ProgrammeDetailsModel,
  ProgrammeDetailsSchema,
} from '@/schemas/trainee/programme-details';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import {
  SmartSpaceChecklistProps,
  SmartSpaceChecklistStepsSteps,
} from '../../smart-space-checklist.types';
import { traineeSelectors } from '@/store/trainee';
import tool_R4c_form from '@/assets/tool_R4c_form.pdf';
import tool_R4b_form from '@/assets/tool_R4b_form.pdf';
import tool_R4a_form from '@/assets/tool_R4a_form.pdf';
import { useAppDispatch } from '@/store';
import { newGuid } from '@utils/common/uuid.utils';
import {
  classroomsActions,
  classroomsSelectors,
  classroomsThunkActions,
} from '@/store/classroom';
import { cloneDeep } from 'lodash';

export const ProgrammeDetails: React.FC<SmartSpaceChecklistProps> = ({
  setSectionQuestions,
  setVisitSection,
  setActiveStep,
  onSubmitAndContinue,
  checklistVisitId,
}) => {
  const {
    getValues: getProgrammeFormValues,
    setValue: setProgrammeFormValue,
    register: programmeFormRegister,
    trigger: triggerR4bForm,
    control: programmeFormControl,
  } = useForm<ProgrammeDetailsModel>({
    resolver: yupResolver(ProgrammeDetailsSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });
  const provinces = useSelector(staticDataSelectors.getProvinces);
  const { isOnline } = useOnlineStatus();
  const { isValid, errors } = useFormState({ control: programmeFormControl });
  const {
    haveTheTitleDeeds,
    ownTheProperty,
    unproclaimedLand,
    liveAtTheProperty,
    popiaConsent,
    haveReadTheSmartStarterInformation,
  } = useWatch<ProgrammeDetailsModel>({
    control: programmeFormControl,
    defaultValue: {},
  });
  const [articleTitle, setArticleTitle] = useState<string>();
  const [presentArticle, setPresentArticle] = useState<boolean>(false);
  const [contentConsentTypeEnum, setContentConsentTypeEnum] =
    useState<ContentConsentTypeEnum>();
  const [r4bPhotoUrl, setR4bPhotoUrl] = useState<string>();
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const [photoQuestionNumber, setPhotoQuestionNumber] = useState<number>();
  const acceptedFormats = ['jpg, bmp'];
  const visitData = useSelector(
    traineeSelectors.getTraineeVisitData(checklistVisitId)
  );

  const [displayPhotoDeleteWarning, setDisplayPhotoDeleteWarning] =
    useState<boolean>(false);

  const classroom = useSelector(classroomsSelectors.getClassroom);
  const [editedAddress, setEditedAddress] = useState(
    classroom?.siteAddress?.addressLine1 || ''
  );

  const appDispatch = useAppDispatch();

  const [questions, setAnswers] = useState([
    {
      question:
        'I have read the SmartStarter Information Sheet on Child Protection and Confidentiality',
      answer: false,
    },
    {
      question:
        'By submitting your personal information on this site, you consent to the collection, processing and storage of your data in accordance with our Privacy Policy and Protection of Personal Information Act (POPIA).',
      answer: false,
    },
    {
      question: 'What is the name of your programme?',
      answer: '',
    },
    {
      question: ' What type of programme are you running or planning to run?',
      answer: '',
    },
    {
      question: 'Ward number',
      answer: '',
    },
    {
      question: 'Address line 1',
      answer: '',
    },
    {
      question: 'Address line 2',
      answer: '',
    },
    {
      question: 'City',
      answer: '',
    },
    {
      question: 'Province',
      answer: '',
    },
    {
      question: 'Postal Code',
      answer: '',
    },
    {
      question:
        'Do you own the property where you will run your SmartStart programme?',
      answer: '',
    },
    {
      question: 'Do you have the Title Deeds for the property?',
      answer: '',
    },
    {
      question: 'Is the property on un-proclaimed land?',
      answer: '',
    },
    {
      question: 'Take a photo of a certified copy of the Title Deeds',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4c Affidavit - Property Ownership',
      answer: '',
    },
    {
      question: 'Do you live at the property?',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4a form - Agreement for premises to be uesd for early learning programme.',
      answer: '',
    },
    {
      question:
        'Take a photo of the filled and signed R4b form - Confirmation of Lease.',
      answer: '',
    },
    {
      question: 'Area Classification',
      answer: '',
    },
    {
      question: 'Area Sub Classification',
      answer: '',
    },
    {
      question: 'Area',
      answer: '',
    },
    {
      question: 'Municipality',
      answer: '',
    },
  ]);
  const visitSection = 'Programme details';

  const completedItems = visitData
    ?.filter((item) => item?.visitSection === visitSection)
    .filter(
      (item) =>
        item?.questionAnswer === 'true' ||
        (item?.questionAnswer !== ' ' &&
          item?.questionAnswer !== 'false' &&
          item?.questionAnswer !== '')
    );

  const disableSection = completedItems?.length
    ? completedItems?.length >= 6
    : false;

  const checkedquestion = (question: string) => {
    const isChecked = visitData?.find((item) => item?.question === question);
    return isChecked;
  };

  const programData = useSelector(staticDataSelectors.getProgrammeTypes);

  const displayArticle = async (
    key: ContentConsentTypeEnum | undefined,
    title: string
  ) => {
    if (!key) {
      setPresentArticle(true);
      setArticleTitle(title);
      return;
    }

    setContentConsentTypeEnum(key);
    setPresentArticle(true);
    setArticleTitle(title);
  };

  const onOptionSelected = useCallback(
    (value, index) => {
      const currentQuestion = questions[index];

      const updatedQuestions = questions.map((question) => {
        if (question?.question === currentQuestion.question) {
          return {
            ...question,
            answer: value,
          };
        }
        return question;
      });

      setAnswers(updatedQuestions);
      setSectionQuestions?.([
        {
          visitSection,
          questions: updatedQuestions,
        },
      ]);
      triggerR4bForm();
    },

    [questions, setSectionQuestions]
  );

  const areaClassifications = ['Urban', 'Rural', 'Informal Settlement'];

  const areasSubClassifications = [
    'Metro',
    'City',
    'Town',
    'Village',
    'Informal Settlement',
  ];

  const setPhotoUrl = (imageUrl: string) => {
    setProgrammeFormValue('r4bPhoto', imageUrl);

    setR4bPhotoUrl(imageUrl);
    triggerR4bForm();
    setPhotoActionBarVisible(false);
  };

  const sortedVisitData = Array.isArray(visitData)
    ? visitData.slice().sort((a, b) => {
        const questionA = (a?.question ?? '').toLowerCase();
        const questionB = (b?.question ?? '').toLowerCase();
        return questionA.localeCompare(questionB);
      })
    : [];

  const deleteBirthDocumentPhoto = () => {
    setProgrammeFormValue('r4bPhoto', '');
    setR4bPhotoUrl('');
    setDisplayPhotoDeleteWarning(false);
  };

  const changeSmartSpaceCheckAddress = async () => {
    const classroomCopy = cloneDeep(classroom);
    if (classroomCopy) {
      const siteAddressId =
        classroomCopy.siteAddressId || classroom?.siteAddress?.id || newGuid();

      const siteAddress: SiteAddressDto = {
        id: siteAddressId,
        name: getProgrammeFormValues().programmeName || '',
        areaClassification: getProgrammeFormValues().areaClassification || '',
        areaSubClassification:
          getProgrammeFormValues().areaSubClassification || '',
        addressLine1: getProgrammeFormValues().addressLine1 || '',
        addressLine2: getProgrammeFormValues().addressLine2 || '',
        provinceId: getProgrammeFormValues().provinceId || '',
        municipality: getProgrammeFormValues().municipality || '',
        area: getProgrammeFormValues().area || '',
        addressLine3: getProgrammeFormValues().addressLine3 || '', // City
        postalCode: getProgrammeFormValues().postalCode || '',
        ward: getProgrammeFormValues().ward || '',
      };
      classroomCopy.siteAddress = siteAddress;
      classroomCopy.siteAddressId = siteAddressId;

      if (classroomCopy.siteAddress) {
        appDispatch(
          classroomsActions.updateClassroomSiteAddress(
            classroomCopy as ClassroomDto
          )
        );
        if (isOnline) {
          await appDispatch(
            classroomsThunkActions.upsertClassroomSiteAddress({})
          );
        }
      }
    }
  };

  useEffect(() => {
    if (!visitData || !sortedVisitData || !Array.isArray(visitData)) return;

    const criteriaMap = [
      {
        field: 'haveReadTheSmartStarterInformation' as const,
        match:
          'smartstarter information sheet on child protection and confidentiality',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'popiaConsent' as const,
        match: 'By submitting your personal information on this site',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'programmeName' as const,
        match: 'what is the name of',
        transform: (answer: any) => answer,
      },
      {
        field: 'municipality' as const,
        match: 'municipality',
        transform: (answer: any) => answer,
      },
      {
        field: 'programmeType' as const,
        match: 'type of programme',
        transform: (answer: any) => answer,
      },
      {
        field: 'area' as const,
        match: 'area',
        transform: (answer: any) => answer,
      },
      {
        field: 'ward' as const,
        match: 'ward',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine1' as const,
        match: 'address line 1',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine2' as const,
        match: 'address line 2',
        transform: (answer: any) => answer,
      },
      {
        field: 'addressLine3' as const,
        match: 'city',
        transform: (answer: any) => answer,
      },
      {
        field: 'provinceId' as const,
        match: 'province',
        transform: (answer: any) => answer,
      },
      {
        field: 'postalCode' as const,
        match: 'postal code',
        transform: (answer: any) => answer,
      },
      {
        field: 'ownTheProperty' as const,
        match: 'own the property',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'haveTheTitleDeeds' as const,
        match: 'have the title deeds',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => !!answer,
      },
      {
        field: 'unproclaimedLand' as const,
        match: 'un-proclaimed land',
        transform: (answer: any) => answer,
      },
      {
        field: 'liveAtTheProperty' as const,
        match: 'live at the property?',
        transform: (answer: any) => answer,
      },
    ];

    criteriaMap.forEach(({ field, match, transform }) => {
      const found = sortedVisitData.find((x) =>
        x.question?.toLowerCase().includes(match.toLowerCase())
      );

      if (found?.questionAnswer != null) {
        setProgrammeFormValue(field, transform(found.questionAnswer));
      }
    });

    const r4bPhotoMatch = sortedVisitData.find(
      (x) =>
        x.question?.toLowerCase().includes('take a photo') &&
        x.questionAnswer != null &&
        x.questionAnswer !== ''
    );

    if (r4bPhotoMatch && r4bPhotoMatch.questionAnswer) {
      setProgrammeFormValue('r4bPhoto', r4bPhotoMatch.questionAnswer);
      setR4bPhotoUrl(r4bPhotoMatch.questionAnswer);
    }
  }, [visitData]);

  useEffect(() => {
    if (ownTheProperty === false) {
      setProgrammeFormValue('haveTheTitleDeeds', false);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  useEffect(() => {
    if (ownTheProperty === true) {
      setProgrammeFormValue('liveAtTheProperty', undefined);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  const onDownloadImageR4c = () => {
    const pdfUrl = tool_R4c_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4b = () => {
    const pdfUrl = tool_R4b_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4a = () => {
    const pdfUrl = tool_R4a_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4a_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const handlePhotoActive = () => {
    setPhotoActionBarVisible(true);
  };

  return (
    <>
      <BannerWrapper
        showBackground={false}
        size="medium"
        renderBorder={true}
        title={'Programme details'}
        subTitle={'Step 1 of 4'}
        color={'primary'}
        onBack={() => setActiveStep(SmartSpaceChecklistStepsSteps.INITIAL)}
        displayOffline={!isOnline}
        className="pb-16"
      >
        <div
          className={`p-4 ${
            Boolean(checkedquestion(questions?.[0].question))
              ? 'pointer-events-none'
              : ''
          }`}
        >
          <Typography
            type={'h2'}
            text={'Programme details'}
            color={'textDark'}
            className={'my-3'}
          />

          {disableSection && (
            <Alert
              className="my-4"
              type="warning"
              title="You are viewing this form and cannot edit responses."
              list={['This form should be filled in by the trainee.']}
            />
          )}
          <Typography
            type={'h4'}
            text={'Child protection and confidentiality'}
            color={'textDark'}
            className={'my-3'}
          />
          <div className="flex items-center">
            <div className="flex w-full flex-row items-center justify-start">
              <div
                className="flex items-start gap-2"
                onClick={() => {
                  if (!disableSection) {
                    // Prevents functionality if disableSection is true
                    setProgrammeFormValue(
                      'haveReadTheSmartStarterInformation',
                      !haveReadTheSmartStarterInformation
                    );
                    onOptionSelected(!haveReadTheSmartStarterInformation, 0);
                  }
                }}
              >
                <Checkbox<ProgrammeDetailsModel>
                  checked={
                    Boolean(
                      checkedquestion(questions?.[0].question)?.questionAnswer
                    ) ||
                    getProgrammeFormValues().haveReadTheSmartStarterInformation
                  }
                  register={programmeFormRegister}
                  nameProp={'haveReadTheSmartStarterInformation'}
                  value={
                    String(
                      getProgrammeFormValues()
                        .haveReadTheSmartStarterInformation
                    ) ||
                    checkedquestion(questions?.[0].question)?.questionAnswer!
                  }
                  disabled={Boolean(checkedquestion(questions?.[0].question))}
                ></Checkbox>
                <Typography
                  text={questions?.[0].question}
                  type="body"
                  color={'textMid'}
                />
              </div>
            </div>

            <Typography
              type={'body'}
              text={'View'}
              color={'secondary'}
              className={'my-3'}
              underline={true}
              onClick={() => {
                displayArticle(
                  ContentConsentTypeEnum.ChildProtection,
                  'Child Protection'
                );
              }}
            />
          </div>

          <Typography
            type={'h4'}
            text={'Protection of Personal Information Act, 4 of 2023 (POPIA)'}
            color={'textDark'}
            className={'my-3'}
          />

          <div className="flex items-center">
            <div className="flex w-full flex-row items-center justify-start">
              <div
                className="flex items-start gap-2"
                onClick={() => {
                  if (!disableSection) {
                    // Prevents functionality if disableSection is true
                    setProgrammeFormValue('popiaConsent', !popiaConsent);
                    onOptionSelected(!popiaConsent, 1);
                  }
                }}
              >
                <Checkbox<ProgrammeDetailsModel>
                  checked={
                    Boolean(
                      checkedquestion(questions?.[1].question)?.questionAnswer
                    ) || getProgrammeFormValues().popiaConsent
                  }
                  register={programmeFormRegister}
                  nameProp={'popiaConsent'}
                  value={
                    String(getProgrammeFormValues().popiaConsent) ||
                    checkedquestion(questions?.[1].question)?.question!
                  }
                  disabled={Boolean(checkedquestion(questions?.[1].question))}
                ></Checkbox>
                <Typography
                  text={questions?.[1].question}
                  type="body"
                  color={'textMid'}
                />
              </div>
            </div>

            <Typography
              type={'body'}
              text={'View'}
              color={'secondary'}
              className={'my-3'}
              underline={true}
              onClick={() => {
                displayArticle(ContentConsentTypeEnum.POPIA, 'Coming soon...');
              }}
            />
          </div>
        </div>
        <div className="mt-2 space-y-4 p-4">
          <>
            <FormInput<ProgrammeDetailsModel>
              label={questions?.[2].question}
              error={errors['programmeName']}
              register={programmeFormRegister}
              nameProp={'programmeName'}
              placeholder={'E.g. Little Lambs Preschool'}
              type={'text'}
              onChange={(e) => {
                var value = (e.target as HTMLInputElement).value;
                setProgrammeFormValue('programmeName', value);
                onOptionSelected(value, 2);
              }}
              disabled={disableSection}
            ></FormInput>
            <div
              className={`w-full ${
                Boolean(checkedquestion(questions?.[3].question))
                  ? 'pointer-events-none'
                  : ''
              }`}
            >
              <label className={styles.label}>{questions?.[3].question}</label>
              <div className="mt-1">
                <Controller
                  name={'programmeType'}
                  control={programmeFormControl}
                  render={({ field: { onChange, value, ref } }) => (
                    <ButtonGroup<string | string[]>
                      inputRef={ref}
                      options={
                        (programData &&
                          programData.map((x: ProgrammeTypeDto) => {
                            return { text: x.description, value: x.id ?? '' };
                          })) ||
                        []
                      }
                      multiple={false}
                      onOptionSelected={(e) => {
                        setProgrammeFormValue('programmeType', e as string);
                        onOptionSelected(e, 3);
                      }}
                      selectedOptions={value}
                      color="secondary"
                      type={ButtonGroupTypes.Button}
                      className={`w-full ${
                        disableSection ? 'pointer-events-none' : ''
                      }`}
                    />
                  )}
                ></Controller>
              </div>
            </div>
            <div
              className={`${
                // Boolean(checkedquestion(questions?.[10].question))
                Boolean(checkedquestion(questions?.[18].question))
                  ? 'pointer-events-none'
                  : ''
              }`}
            >
              <Dropdown
                placeholder={'Area Classification'}
                list={
                  (areaClassifications &&
                    areaClassifications.map((area: string) => ({
                      label: area,
                      value: area || '',
                    }))) ||
                  []
                }
                disabled={disableSection}
                fillType="clear"
                fullWidth={true}
                label={'Area Classification'}
                className={classNames(styles.divider, 'w-full')}
                selectedValue={
                  sortedVisitData.length > 0
                    ? sortedVisitData.find(
                        (x) =>
                          x.question?.toLowerCase() === 'area classification'
                      )?.questionAnswer ||
                      getProgrammeFormValues().areaClassification
                    : getProgrammeFormValues().areaClassification
                }
                onChange={(value: string) => {
                  setProgrammeFormValue('areaClassification', value);
                  onOptionSelected(value, 18);
                }}
              />

              <Dropdown
                placeholder={'Area Sub Classification'}
                list={
                  (areasSubClassifications &&
                    areasSubClassifications.map((area: string) => ({
                      label: area,
                      value: area || '',
                    }))) ||
                  []
                }
                disabled={disableSection}
                fillType="clear"
                fullWidth={true}
                label={'Area Sub Classification'}
                className={classNames(styles.divider, 'w-full')}
                selectedValue={
                  sortedVisitData.length > 0
                    ? sortedVisitData.find(
                        (x) =>
                          x.question?.toLowerCase() ===
                          'area sub classification'
                      )?.questionAnswer ||
                      getProgrammeFormValues().areaSubClassification
                    : getProgrammeFormValues().areaSubClassification
                }
                onChange={(value: string) => {
                  setProgrammeFormValue('areaSubClassification', value);
                  onOptionSelected(value, 19);
                }}
              />

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[5].question}
                  error={errors['addressLine1']}
                  register={programmeFormRegister}
                  nameProp={'addressLine1'}
                  placeholder={'E.g. 11 Green Road'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('addressLine1', value);
                    onOptionSelected(value, 5);
                  }}
                  disabled={disableSection}
                ></FormInput>
              </div>

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[6].question}
                  error={errors['addressLine2']}
                  register={programmeFormRegister}
                  nameProp={'addressLine2'}
                  placeholder={'E.g. Mamelodi East'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('addressLine2', value);
                    onOptionSelected(value, 6);
                  }}
                  disabled={disableSection}
                ></FormInput>
              </div>

              <Dropdown
                placeholder={'Choose province'}
                list={
                  (provinces &&
                    provinces.map((province: ProvinceDto) => ({
                      label: province.description,
                      value: province.id || '',
                    }))) ||
                  []
                }
                disabled={disableSection}
                fillType="clear"
                fullWidth={true}
                label={'Province'}
                className={classNames(styles.divider, 'w-full')}
                selectedValue={getProgrammeFormValues().provinceId}
                onChange={(value: string) => {
                  setProgrammeFormValue('provinceId', value);
                  onOptionSelected(value, 8);
                }}
              />

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[21].question}
                  error={errors['municipality']}
                  register={programmeFormRegister}
                  nameProp={'municipality'}
                  disabled={disableSection}
                  placeholder={'e.g. City of Cape Town Metropolitan'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('municipality', value);
                    onOptionSelected(value, 21);
                  }}
                ></FormInput>
              </div>

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[20].question}
                  error={errors['area']}
                  register={programmeFormRegister}
                  nameProp={'area'}
                  disabled={disableSection}
                  placeholder={'e.g. Bellville'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('area', value);
                    onOptionSelected(value, 20);
                  }}
                ></FormInput>
              </div>

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[7].question}
                  register={programmeFormRegister}
                  error={errors['addressLine3']}
                  nameProp={'addressLine3'}
                  placeholder={'E.g. Cape Town'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('addressLine3', value);
                    onOptionSelected(value, 7);
                  }}
                  disabled={disableSection}
                ></FormInput>
              </div>

              <div className="mt-4">
                <FormInput<ProgrammeDetailsModel>
                  label={questions?.[9].question}
                  error={errors['postalCode']}
                  register={programmeFormRegister}
                  nameProp={'postalCode'}
                  placeholder={'E.g. 0081'}
                  type={'text'}
                  onChange={(e) => {
                    var value = (e.target as HTMLInputElement).value;
                    setProgrammeFormValue('postalCode', value);
                    onOptionSelected(value, 9);
                  }}
                  disabled={disableSection}
                ></FormInput>
              </div>
            </div>

            <div className="mt-4">
              <FormInput<ProgrammeDetailsModel>
                label={questions?.[4].question}
                error={errors['ward']}
                register={programmeFormRegister}
                nameProp={'ward'}
                placeholder={'e.g. 0721'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('ward', value);
                  onOptionSelected(value, 4);
                }}
                disabled={disableSection}
              ></FormInput>
            </div>

            <div className={'w-full'}>
              <label className={styles.label}>{questions?.[10].question}</label>
              <div
                className={`mt-1 ${
                  Boolean(checkedquestion(questions?.[10].question))
                    ? 'pointer-events-none'
                    : ''
                }`}
              >
                <ButtonGroup<boolean | undefined>
                  options={yesNoOptions}
                  onOptionSelected={(value: any) => {
                    setProgrammeFormValue('ownTheProperty', value as boolean, {
                      shouldValidate: true,
                    });
                    onOptionSelected(value, 10);
                  }}
                  selectedOptions={[getProgrammeFormValues().ownTheProperty]}
                  color="secondary"
                  type={ButtonGroupTypes.Button}
                  className={`w-full ${
                    disableSection ? 'pointer-events-none' : ''
                  }`}
                />
              </div>
            </div>

            {ownTheProperty === true && (
              <div className={'w-full'}>
                <label className={styles.label}>
                  {questions?.[11].question}
                </label>
                <div
                  className={`mt-1 ${
                    Boolean(checkedquestion(questions?.[11].question))
                      ? 'pointer-events-none'
                      : ''
                  }`}
                >
                  <ButtonGroup<boolean | undefined>
                    options={yesNoOptions}
                    onOptionSelected={(value: any) => {
                      setProgrammeFormValue(
                        'haveTheTitleDeeds',
                        value as boolean,
                        {
                          shouldValidate: true,
                        }
                      );
                      onOptionSelected(value, 11);
                    }}
                    selectedOptions={[
                      (sortedVisitData &&
                      sortedVisitData.length > 0 &&
                      sortedVisitData.find((v) =>
                        v.question
                          ?.toLowerCase()
                          .includes('have the title deeds')
                      )?.questionAnswer
                        ? !!sortedVisitData.find((v) =>
                            v.question
                              ?.toLowerCase()
                              .includes('have the title deeds')
                          )?.questionAnswer
                        : getProgrammeFormValues().haveTheTitleDeeds) ?? false,
                    ]}
                    color="secondary"
                    type={ButtonGroupTypes.Button}
                    className={`w-full ${
                      disableSection ? 'pointer-events-none' : ''
                    }`}
                  />
                </div>
              </div>
            )}
            {ownTheProperty === false && (
              <div className={'w-full'}>
                <label className={styles.label}>
                  {questions?.[15].question}
                </label>
                <div
                  className={`mt-1 ${
                    Boolean(checkedquestion(questions?.[15].question))
                      ? 'pointer-events-none'
                      : ''
                  }`}
                >
                  <ButtonGroup<boolean | undefined>
                    options={yesNoOptions}
                    onOptionSelected={(value: any) => {
                      setProgrammeFormValue(
                        'liveAtTheProperty',
                        value as boolean,
                        {
                          shouldValidate: true,
                        }
                      );
                      onOptionSelected(value, 15);
                    }}
                    selectedOptions={[
                      getProgrammeFormValues().liveAtTheProperty,
                    ]}
                    color="secondary"
                    type={ButtonGroupTypes.Button}
                    className={`w-full ${
                      disableSection ? 'pointer-events-none' : ''
                    }`}
                  />
                </div>
              </div>
            )}
            {liveAtTheProperty === true && !disableSection && (
              <>
                <Alert
                  className="mb-4"
                  type="info"
                  title={`The owner or landlord of the property will need to fill in the “confirmation of lease” with you (form R4b).`}
                  list={[
                    'Fill in the form with your landlord and upload a picture of it below.',
                  ]}
                  button={
                    <Button
                      onClick={onDownloadImageR4b}
                      text="Download the R4b form"
                      icon="DownloadIcon"
                      type={'filled'}
                      color={'primary'}
                      textColor={'white'}
                      disabled={disableSection}
                    />
                  }
                />
                <ImageInput<ProgrammeDetailsModel>
                  acceptedFormats={acceptedFormats}
                  resolutionLimit={IMAGE_WIDTH}
                  label={questions?.[17].question}
                  nameProp="r4bPhoto"
                  icon="CameraIcon"
                  className={'py-4'}
                  currentImageString={r4bPhotoUrl}
                  register={programmeFormRegister}
                  overrideOnClick={() => setPhotoActionBarVisible(true)}
                  onValueChange={(imageString: string) => {
                    setPhotoQuestionNumber(17);
                    setProgrammeFormValue('r4bPhoto', imageString);
                    triggerR4bForm();
                  }}
                  disabled={disableSection}
                ></ImageInput>
              </>
            )}
            {liveAtTheProperty === false && (
              <div className={disableSection ? 'pointer-events-none' : ''}>
                <Alert
                  className="mb-4"
                  type="info"
                  title={`The owner or landlord of the property will need to fill in the “Agreement for premises to be used for early learning programme” with you (form 4a).`}
                  list={[
                    'Fill in the form with your landlord and upload a picture of it below.',
                  ]}
                  button={
                    <Button
                      onClick={onDownloadImageR4a}
                      text="Download the R4a form"
                      icon="DownloadIcon"
                      type={'filled'}
                      color={'primary'}
                      textColor={'white'}
                      disabled={disableSection}
                    />
                  }
                />
                <ImageInput<ProgrammeDetailsModel>
                  acceptedFormats={acceptedFormats}
                  resolutionLimit={IMAGE_WIDTH}
                  label={questions?.[16].question}
                  nameProp="r4bPhoto"
                  icon="CameraIcon"
                  className={'py-4'}
                  currentImageString={r4bPhotoUrl}
                  register={programmeFormRegister}
                  overrideOnClick={() => {
                    setPhotoQuestionNumber(16);
                    handlePhotoActive();
                  }}
                  onValueChange={(imageString: string) => {
                    setProgrammeFormValue('r4bPhoto', imageString);
                    triggerR4bForm();
                  }}
                  disabled={disableSection}
                ></ImageInput>
              </div>
            )}
          </>
          {haveTheTitleDeeds === false && ownTheProperty === true && (
            <div className={'w-full'}>
              <label className={styles.label}>{questions?.[12].question}</label>
              <div
                className={`mt-1 ${
                  Boolean(checkedquestion(questions?.[12].question))
                    ? 'pointer-events-none'
                    : ''
                }`}
              >
                <Controller
                  name="unproclaimedLand"
                  control={programmeFormControl}
                  render={({ field: { onChange, value, ref } }) => (
                    <ButtonGroup<boolean>
                      inputRef={ref}
                      options={yesNoOptions}
                      onOptionSelected={(value: any) => {
                        setProgrammeFormValue(
                          'unproclaimedLand',
                          value as boolean,
                          {
                            shouldValidate: true,
                          }
                        );
                        onOptionSelected(value, 12);
                      }}
                      selectedOptions={value}
                      color="secondary"
                      type={ButtonGroupTypes.Button}
                      className={'w-full'}
                    />
                  )}
                />
              </div>
            </div>
          )}

          {haveTheTitleDeeds === true && (
            <ImageInput<ProgrammeDetailsModel>
              acceptedFormats={acceptedFormats}
              resolutionLimit={IMAGE_WIDTH}
              label={questions?.[13].question}
              nameProp="r4bPhoto"
              icon="CameraIcon"
              className={'py-4'}
              currentImageString={r4bPhotoUrl}
              register={programmeFormRegister}
              overrideOnClick={() => {
                setPhotoQuestionNumber(13);
                handlePhotoActive();
              }}
              onValueChange={(imageString: string) => {
                setProgrammeFormValue('r4bPhoto', imageString);
                triggerR4bForm();
              }}
              disabled={disableSection}
            ></ImageInput>
          )}

          {unproclaimedLand === false && !disableSection && (
            <Alert
              className="mb-4"
              type="warning"
              title={`Apply to your municipality for a copy of the title deeds.`}
              list={[
                'You will need to get a copy of the title deeds in order to complete this section.',
                'Once you have the title deeds, answer “Yes” to the Title Deeds question above and add a photo of the document.',
              ]}
            />
          )}

          {unproclaimedLand === true && (
            <div className={disableSection ? 'pointer-events-none' : ''}>
              <Alert
                className="mb-4"
                type="info"
                title={`Please get a signed, stamped copy of an R4c form instead.`}
                list={[
                  'This form can be stamped by any commissioner of oaths. This could include: police officers, South African Post Office workers, lawyers, or accountants for example.',
                ]}
                button={
                  <Button
                    onClick={onDownloadImageR4c}
                    text="Download the R4c form"
                    icon="DownloadIcon"
                    type={'filled'}
                    color={'primary'}
                    textColor={'white'}
                  />
                }
              />
              <ImageInput<ProgrammeDetailsModel>
                acceptedFormats={acceptedFormats}
                resolutionLimit={IMAGE_WIDTH}
                label={questions?.[14].question}
                nameProp="r4bPhoto"
                icon="CameraIcon"
                className={'py-4'}
                currentImageString={r4bPhotoUrl}
                register={programmeFormRegister}
                overrideOnClick={() => {
                  setPhotoQuestionNumber(14);
                  handlePhotoActive();
                }}
                onValueChange={(imageString: string) => {
                  setProgrammeFormValue('r4bPhoto', imageString);
                  triggerR4bForm();
                }}
              ></ImageInput>
            </div>
          )}
          <div>
            <div>
              <Button
                type="filled"
                color="primary"
                className={styles.button}
                disabled={!isValid || disableSection}
                onClick={() => {
                  changeSmartSpaceCheckAddress();
                  setVisitSection(visitSection);
                  onSubmitAndContinue();
                }}
              >
                {renderIcon('ArrowCircleRightIcon', 'mr-2 text-white w-5')}
                <Typography
                  type={'help'}
                  text={'Save & continue'}
                  color={'white'}
                />
              </Button>
            </div>
          </div>
        </div>
      </BannerWrapper>
      {contentConsentTypeEnum && (
        <Article
          consentEnumType={contentConsentTypeEnum}
          visible={presentArticle}
          title={articleTitle}
          onClose={() => setPresentArticle(false)}
          isOpen={true}
        />
      )}
      {/* <Dialog visible={showMap} position={DialogPosition.Bottom} stretch>
        <AddressMap
          onClose={() => setShowMap?.(false)}
          onSubmit={(address) => {
            setProgrammeFormValue('programmeAddress', address);
            setEditedAddress(address);
            onOptionSelected(address, 3);
          }}
        />
      </Dialog> */}
      <Dialog
        visible={photoActionBarVisible}
        position={DialogPosition.Bottom}
        stretch
      >
        <div>
          <PhotoPrompt
            title={'Upload image'}
            resolutionLimit={IMAGE_WIDTH}
            onClose={() => setPhotoActionBarVisible(false)}
            onAction={(imageUrl: string) => {
              setPhotoUrl(imageUrl);
              onOptionSelected(imageUrl, photoQuestionNumber);
            }}
            onDelete={() => deleteBirthDocumentPhoto()}
          ></PhotoPrompt>
        </div>
      </Dialog>
    </>
  );
};
